// Marketing copy for the "Discover Syndiqia" page (FR first, EN). Kept apart from messages.ts: it is long-form
// content, not interface text. Keep the availability flags honest: "now" only for what the demo really does.

import type { AreaIconName } from '../components/AreaIcon';

export type Availability = 'now' | 'soon';

export interface AgentArea {
  icon: AreaIconName;
  title: string;
  availability: Availability;
  /** What the agents do on their own. */
  automated: string[];
  /** What a person approves or decides. */
  human: string;
}

export interface AboutContent {
  eyebrow: string;
  title: string;
  lead: string;
  openApp: string;
  seeAgents: string;
  heroPoints: string[];
  video: { cta: string; title: string; lead: string; label: string };
  showcase: { eyebrow: string; title: string; text: string; cta: string; label: string; video: string; videoLabel: string; close: string };
  problem: { title: string; items: { title: string; text: string }[] };
  agents: { title: string; lead: string; automatedLabel: string; humanLabel: string; availability: Record<Availability, string>; areas: AgentArea[] };
  flow: { title: string; lead: string; steps: { title: string; text: string }[] };
  humans: { title: string; lead: string; items: string[]; approvalTitle: string; approvalItems: string[] };
  trust: { title: string; items: { title: string; text: string }[] };
  offers: { title: string; lead: string; plans: { name: string; audience: string; price: string; priceNote: string; features: string[]; highlight?: boolean }[]; terms: string[]; note: string };
  faq: { title: string; items: { q: string; a: string }[] };
  closing: { title: string; text: string };
  confidential: string;
  back: string;
}

const fr: AboutContent = {
  eyebrow: 'Syndicats de copropriété et associations de propriétaires du Québec',
  title: 'L’administration de votre syndicat ou de votre association, prise en charge par des agents. Les décisions restent les vôtres.',
  lead:
    'Syndiqia automatise l’administration courante d’un syndicat de copropriété ou d’une association de propriétaires : cotisations, relances, conciliation bancaire, demandes des propriétaires, documents, entretien, conformité, assemblées et réservations. Le conseil d’administration approuve ce qui compte et garde la main sur tout le reste.',
  openApp: 'Ouvrir la démo',
  seeAgents: 'Ce que les agents font',
  heroPoints: ['En français d’abord, bilingue', 'Conçu pour le Code civil, la Loi 16 et la Loi 25', 'Fonds de prévoyance strictement séparé', 'Données hébergées au Canada'],

  video: {
    cta: 'Voir la vidéo (3 min)',
    title: 'Syndiqia en trois minutes',
    lead: 'Une soirée de bénévole au conseil, avant et après Syndiqia.',
    label: 'Vidéo de présentation de Syndiqia',
  },

  showcase: {
    eyebrow: 'Carte interactive',
    title: 'Toutes les automatisations, sur une seule carte',
    text: 'D’où vient le travail, ce que Syndiqia en fait et où le conseil intervient, avec trois dossiers suivis pas à pas : une question, une facture, un paiement en retard.',
    cta: 'Explorer la carte',
    label: 'Aperçu animé de la carte des automatisations',
    video: 'Voir la vidéo',
    videoLabel: 'Vidéo : la carte des automatisations en une minute',
    close: 'Fermer',
  },

  problem: {
    title: 'Administrer une copropriété ou un domaine, c’est un deuxième emploi',
    items: [
      {
        title: 'Des bénévoles débordés',
        text: 'Les administrateurs passent leurs soirées à facturer, relancer, répondre aux mêmes questions et chercher le bon article de la déclaration.',
      },
      {
        title: 'Des obligations qui s’accumulent',
        text: 'La Loi 16 impose aux syndicats le carnet d’entretien et l’étude du fonds de prévoyance, à obtenir d’ici la mi-août 2028, ainsi que le registre de la copropriété. Une échéance manquée coûte cher.',
      },
      {
        title: 'Une gestion externe coûteuse',
        text: 'Les charges de copropriété ont presque doublé en dix ans et les frais de gestion suivent, alors qu’une grande partie du travail est administrative, répétitive et automatisable.',
      },
    ],
  },

  agents: {
    title: 'Ce que les agents prennent en charge',
    lead: 'Chaque agent travaille avec les mêmes règles et les mêmes permissions qu’une personne du conseil. Vous choisissez, processus par processus, son degré d’autonomie : au départ il prépare et vous confirmez; quand vous lui faites confiance, il agit seul, dans des garde-fous précis, et chaque action automatique est consignée.',
    automatedLabel: 'Automatisé',
    humanLabel: 'Vous',
    availability: { now: 'Dans la démo', soon: 'À venir' },
    areas: [
      {
        icon: 'dues',
        title: 'Cotisations et recouvrement',
        availability: 'now',
        automated: [
          'Calcul des cotisations selon les quotes-parts, fonds d’exploitation et fonds de prévoyance séparés',
          'Conciliation automatique des dépôts à partir du relevé bancaire',
          'Relances graduées des retards, en français ou en anglais selon le copropriétaire',
          'Suivi des arriérés jusqu’à la mise en demeure, exclusivement',
        ],
        human: 'Vous approuvez chaque lot de relances avant l’envoi. Les poursuites restent une décision du conseil.',
      },
      {
        icon: 'requests',
        title: 'Demandes et questions',
        availability: 'now',
        automated: [
          'Tri de chaque demande : catégorie, urgence, unité concernée',
          'Brouillon de réponse qui cite l’article et la page de la déclaration ou du règlement',
          'Réponses immédiates aux questions des copropriétaires, 24 h sur 24, à partir des documents du syndicat',
          'Transfert au conseil quand les documents ne répondent pas',
        ],
        human: 'Vous relisez, modifiez ou écartez le brouillon. Rien n’est envoyé en votre nom sans vous.',
      },
      {
        icon: 'documents',
        title: 'Documents et registre',
        availability: 'now',
        automated: [
          'Classement et indexation de la déclaration, des règlements, des procès-verbaux et des attestations',
          'Recherche en texte intégral en français, page par page',
          'Registre des unités, des copropriétaires et des quotes-parts, avec historique',
          'Fiche de chaque copropriétaire et occupant (unités, historique), invitations, rôles et fonctions du conseil (président, trésorier…) avec la durée des mandats, accès selon le rôle',
        ],
        human: 'Vous décidez quels documents sont partagés avec les copropriétaires et lesquels restent au conseil.',
      },
      {
        icon: 'maintenance',
        title: 'Entretien',
        availability: 'now',
        automated: [
          'Pour chaque problème signalé, un bon de travail proposé : composante, fournisseur du bon métier, priorité',
          'Message au fournisseur rédigé, sans les renseignements personnels des copropriétaires',
          'Lien sécurisé pour le fournisseur, sans compte : il accepte, propose une date, joint photos et documents, et signale la fin des travaux',
          'Soumissions : jusqu’à 5 fournisseurs invités, chacun avec son lien; réponses lues par l’agent (prix, validité, exclusions) et comparées dans un tableau',
          'Factures des fournisseurs reçues par courriel à l’adresse de facturation du syndicat, lues, associées au bon de travail et vérifiées : bon fournisseur, travail terminé, montant dans l’estimation, numéro non répété, TPS/TVQ exactes',
          'Calendrier d’entretien préventif et carnet d’entretien alimenté à chaque intervention',
        ],
        human: 'Vous créez le bon en un clic, retenez la soumission, confirmez la fin des travaux et approuvez les factures (ou laissez approuver seules les factures conformes sous un montant que vous fixez); le paiement reste à la banque. Les urgences sont gérées par une personne.',
      },
      {
        icon: 'governance',
        title: 'Assemblées et réunions du conseil',
        availability: 'now',
        automated: [
          'Ordre du jour proposé à partir des demandes à trancher, des échéances, des travaux et des suivis, avec le libellé des résolutions',
          'Avis de convocation à chaque copropriétaire, dans sa langue, avec preuve de remise et contrôle du délai',
          'Enregistrement de la séance transcrit automatiquement (ou transcription Teams/Zoom importée), découpé par point de l’ordre du jour',
          'Procès-verbal rédigé à partir de la transcription et des notes; suivis reportés à la réunion suivante',
          'Feuille de présence tirée du registre, procurations et quorum en direct; droit de vote suspendu après trois mois d’arrérages',
          'Votes pondérés par les quotes-parts, majorité de chaque résolution calculée, résultat inscrit au procès-verbal',
          'Conseil d’administration : calendrier des réunions régulières, quorum et voix prépondérante selon vos règlements, résolutions écrites signées en ligne par tous les administrateurs',
          'Registre des résolutions et livre des procès-verbaux tenus à jour automatiquement, prêts à imprimer',
        ],
        human: 'Vous retenez les points de l’ordre du jour, approuvez l’avis, présidez, obtenez le consentement à l’enregistrement et approuvez le procès-verbal.',
      },
      {
        icon: 'compliance',
        title: 'Conformité',
        availability: 'now',
        automated: [
          'Suivi des obligations de la Loi 16 : carnet d’entretien, étude du fonds de prévoyance, registre',
          'Rappels au conseil à 60, 30 et 7 jours de chaque échéance, puis en cas de retard',
          'Calendrier des échéances légales du syndicat',
          'Attestations d’assurance des copropriétaires : lues automatiquement, suivies unité par unité, rappels avant l’expiration',
        ],
        human: 'Vous recevez les alertes à temps et décidez des suites.',
      },
      {
        icon: 'reservations',
        title: 'Réservations',
        availability: 'now',
        automated: [
          'Réservation de la salle commune, du terrain de pickleball, du gym ou de l’ascenseur de déménagement, par les copropriétaires eux-mêmes',
          'Règles appliquées automatiquement : heures, durée, délai, quota par unité, frais, dépôt, fermetures',
          'Aucune double réservation possible; frais portés au compte de l’unité, remboursés si l’annulation est faite à temps',
        ],
        human: 'Vous fixez les règles et approuvez les réservations qui le demandent (ex. : déménagement). Le reste se fait tout seul.',
      },
    ],
  },

  flow: {
    title: 'Comment travaille un agent',
    lead: 'Prenons un paiement en retard.',
    steps: [
      { title: 'Il détecte', text: 'Le dépôt attendu n’apparaît pas au relevé bancaire importé.' },
      { title: 'Il prépare', text: 'Il calcule le solde, choisit l’étape de relance prévue et rédige l’avis dans la langue du copropriétaire.' },
      { title: 'Vous approuvez', text: 'Le lot de relances attend dans votre file d’approbation. Un clic pour approuver ou refuser.' },
      { title: 'Il exécute et trace', text: 'Les courriels partent avec preuve de remise. Chaque étape est consignée : données, sources, approbateur.' },
    ],
  },

  humans: {
    title: 'Ce qui reste toujours humain',
    lead: 'Certaines décisions demandent du jugement, de la présence ou une responsabilité légale. Syndiqia ne les prend jamais à votre place.',
    items: [
      'La stratégie du fonds de prévoyance',
      'Les cotisations spéciales',
      'L’application des règlements et les amendes',
      'Les différends entre copropriétaires',
      'Les urgences sur place',
      'Tout dossier qui va devant les tribunaux',
    ],
    approvalTitle: 'File d’approbation',
    approvalItems: ['Mouvements d’argent', 'Amendes et pénalités', 'Avis légaux', 'Messages à tous les copropriétaires'],
  },

  trust: {
    title: 'Conçu pour la copropriété québécoise',
    items: [
      { title: 'Code civil du Québec', text: 'Quotes-parts au cœur du système : répartition des charges, votes pondérés, quorum.' },
      { title: 'Associations de propriétaires', text: 'Développements horizontaux en OBNL avec servitudes : règles d’assemblée selon vos règlements, vocabulaire des membres, sans les obligations de la Loi 16.' },
      { title: 'Fonds séparés', text: 'Fonds d’exploitation et fonds de prévoyance strictement séparés, comme l’exige la loi.' },
      { title: 'Grand livre inaltérable', text: 'Comptabilité en partie double ; une écriture ne se modifie jamais, elle se contrepasse.' },
      { title: 'Loi 25', text: 'Données hébergées au Canada, accès selon le rôle, journal des actions.' },
      { title: 'Isolation des syndicats', text: 'Chaque syndicat est cloisonné au niveau de la base de données elle-même.' },
      { title: 'Traçabilité des agents', text: 'Les propositions des agents, les documents cités, ce qu’ils ont fait seuls et qui a approuvé quoi sont consignés. Le journal des événements et des actions automatiques est enchaîné par empreintes : toute modification après coup se détecte, et le conseil peut le vérifier.' },
    ],
  },

  offers: {
    title: 'Trois formules, une seule plateforme',
    lead: 'Les mêmes agents dans chaque formule. Ce qui change : qui fait le suivi et qui se déplace sur place. Prix par porte (logement ou lot) par mois.',
    plans: [
      {
        name: 'Autonome',
        audience: 'Petits syndicats et associations autogérés : le conseil garde la main',
        price: '6 $ par porte par mois',
        priceNote: 'Minimum 49 $ par mois. Mise en service : gratuite en libre-service, 490 $ avec import accompagné',
        features: ['Toute la plateforme et ses agents', 'Application pour le conseil et les propriétaires', 'Soutien par courriel et webinaires'],
      },
      {
        name: 'Assistée',
        audience: 'Conseils qui veulent que le travail soit fait, sans besoin de présence sur place',
        price: '17 $ par porte par mois',
        priceNote: 'Minimum 350 $ par mois. Mise en service : 990 $, offerte avec un contrat de 2 ans',
        features: ['Tout le volet Autonome', 'Un gestionnaire Syndiqia à distance : téléphone, suivi du travail des agents', 'Budget, dossier de fin d’année pour le comptable', 'Préparation des assemblées et présence à distance'],
        highlight: true,
      },
      {
        name: 'Complète',
        audience: 'Conseils qui quittent une firme de gestion à service complet',
        price: '25 $ par porte par mois',
        priceNote: 'Minimum 500 $ par mois. Mise en service : 1 490 $, offerte avec un contrat de 2 ans',
        features: ['Tout le volet Assistée', 'Visites des lieux', 'Supervision des entrepreneurs', 'Urgences 24 h sur 24'],
      },
    ],
    terms: [
      'Essai gratuit de 3 mois, avec vos vraies données',
      'Aucuns frais de résiliation; exportation complète de vos données en tout temps',
      'Prix garanti 24 mois',
      'Aucune commission des fournisseurs; l’argent reste dans le compte bancaire du syndicat ou de l’association',
      'Même prix pour les associations et les développements horizontaux : aucun supplément',
    ],
    note: 'Prix de la phase pilote, taxes en sus. Les frais de paiement préautorisé sont à tarif fixe, jamais en pourcentage.',
  },

  faq: {
    title: 'Questions fréquentes',
    items: [
      { q: 'Un agent peut-il envoyer un message ou payer sans nous?', a: 'Non. Les mouvements d’argent, les avis légaux, les amendes et les messages à tous les copropriétaires passent toujours par la file d’approbation du conseil.' },
      { q: 'D’où viennent les réponses aux copropriétaires?', a: 'Uniquement des documents de votre syndicat partagés avec les copropriétaires : déclaration, règlements, procès-verbaux. Chaque réponse cite l’article et la page. Si les documents ne répondent pas, la question est transférée au conseil.' },
      { q: 'Nos données sont-elles protégées?', a: 'Les données et les fichiers sont hébergés au Canada; ils sont cloisonnés par syndicat ou association au niveau de la base de données, et chaque accès est limité selon le rôle de la personne. Pour lire un document ou rédiger une réponse, des extraits de texte sont traités par le service d’intelligence artificielle d’Amazon, qui peut les traiter hors du Québec : ils n’y sont pas conservés et ne servent pas à entraîner des modèles.' },
      { q: 'Et notre comptable?', a: 'Le grand livre suit un plan comptable adapté aux syndicats ; l’export vers QuickBooks et Sage est prévu.' },
      { q: 'Peut-on laisser les agents agir seuls?', a: 'Oui, processus par processus, quand vous êtes prêts. Tout commence en mode confirmation; la plateforme mesure la fiabilité de chaque processus (combien de propositions vous confirmez telles quelles) et signale quand il est prêt. Un administrateur le passe alors en automatique : réponses aux questions, rappels de paiement, conciliation bancaire, attestations d’assurance, envoi des travaux aux fournisseurs jusqu’au montant que vous fixez, approbation des factures conformes et des réservations. Les garde-fous restent actifs, chaque action est consignée et résumée chaque matin par courriel, et on revient en confirmation en un clic.' },
      { q: 'Les copropriétaires doivent-ils utiliser l’application?', a: 'Non. Chaque syndicat a son adresse courriel : un courriel devient une demande, classée et traitée comme les autres, et les copropriétaires répondent au conseil simplement en répondant à ses courriels.' },
      { q: 'L’équipe Syndiqia peut-elle voir nos données?', a: 'Non, pas sans votre accord. Si vous demandez de l’aide, le soutien demande un accès temporaire; un administrateur l’approuve ou le refuse. L’accès est en lecture seule, chaque page consultée est consignée et vous pouvez le retirer à tout moment.' },
      { q: 'Faut-il une formation?', a: 'Non. Chaque écran a son aide (« ? ») avec un guide par tâche adapté à votre rôle, et l’assistant répond aussi aux questions « comment faire… » dans Syndiqia.' },
      { q: 'Comment se passe le démarrage?', a: 'Vous nous donnez ce que vous avez, dans le format que vous avez : règlements et autres documents en PDF ou Word, liste des propriétaires en Excel, états financiers. Avec notre équipe, en six étapes (identité, documents, propriétaires, finances, fournisseurs, conseil), Syndiqia lit tout, vous dit ce qui est complet et ce qui manque (« il manque le téléphone de 12 propriétaires »), et vous montre ce qu’il a compris. Vous vérifiez et appliquez; rien n’est enregistré avant. Ensuite, on invite les membres.' },
      { q: 'Nous sommes sous contrat avec une firme de gestion. Est-ce trop tard?', a: 'Non. L’essai de 3 mois se fait en parallèle, pendant votre préavis : vous comparez sur vos vrais dossiers, puis nous demandons nous-mêmes les registres à votre gestionnaire actuel. La reprise se fait en 30 jours.' },
      { q: 'Et si Syndiqia cessait ses activités?', a: 'Vos données vous appartiennent : vous pouvez en obtenir une exportation complète en tout temps, sans frais. Votre argent ne transite jamais par Syndiqia : il reste dans votre propre compte bancaire.' },
    ],
  },

  closing: {
    title: 'Moins d’administration, de meilleures décisions.',
    text: 'Explorez les démos : un syndicat fictif de 24 logements et une association de propriétaires d’un domaine, avec leurs finances, leurs demandes et leurs documents. Ensuite, essayez Syndiqia gratuitement pendant 3 mois avec vos propres données.',
  },
  confidential: 'Page de présentation réservée aux utilisateurs connectés pendant la phase de démonstration.',
  back: 'Retour à l’application',
};

const en: AboutContent = {
  eyebrow: 'Quebec condo syndicates and owners’ associations',
  title: "Your syndicate's or association's administration, handled by agents. The decisions stay yours.",
  lead:
    "Syndiqia automates the day-to-day administration of a condo syndicate or an owners' association: dues, reminders, bank reconciliation, owner requests, documents, maintenance, compliance, assemblies and reservations. The board approves what matters and stays in control of everything else.",
  openApp: 'Open the demo',
  seeAgents: 'What the agents do',
  heroPoints: ['French first, fully bilingual', 'Built for the Civil Code, Bill 16 and Law 25', 'Contingency fund strictly separate', 'Data hosted in Canada'],

  video: {
    cta: 'Watch the video (3 min)',
    title: 'Syndiqia in three minutes',
    lead: 'A volunteer director’s evening, before and after Syndiqia. The video is in French.',
    label: 'Syndiqia presentation video',
  },

  showcase: {
    eyebrow: 'Interactive map',
    title: 'Every automation, on a single map',
    text: 'Where the work comes from, what Syndiqia does with it and where the board steps in, with three cases followed step by step: a question, an invoice, a late payment.',
    cta: 'Explore the map',
    label: 'Animated preview of the automation map',
    video: 'Watch the video',
    videoLabel: 'Video: the automation map in one minute',
    close: 'Close',
  },

  problem: {
    title: 'Running a condo or a domain is a second job',
    items: [
      { title: 'Overloaded volunteers', text: 'Directors spend their evenings billing, chasing payments, answering the same questions and looking for the right article of the declaration.' },
      { title: 'Growing obligations', text: 'Bill 16 requires syndicates to obtain a maintenance logbook and a contingency fund study by mid-August 2028, and to keep a building register. A missed deadline is costly.' },
      { title: 'Costly outside management', text: 'Condo fees have nearly doubled in ten years and management fees follow, while much of the work is administrative, repetitive and automatable.' },
    ],
  },

  agents: {
    title: 'What the agents take care of',
    lead: 'Each agent works under the same rules and permissions as a board member. You choose its autonomy process by process: at first it prepares and you confirm; once you trust it, it acts on its own within specific guardrails, and every automatic action is logged.',
    automatedLabel: 'Automated',
    humanLabel: 'You',
    availability: { now: 'In the demo', soon: 'Coming' },
    areas: [
      {
        icon: 'dues',
        title: 'Dues and collections',
        availability: 'now',
        automated: [
          'Dues calculated from the quote-parts, with operating and contingency funds kept separate',
          'Deposits matched automatically from the bank statement',
          "Graduated reminders for late payments, in each co-owner's language",
          'Arrears followed up to, but not including, a formal demand',
        ],
        human: 'You approve each batch of reminders before it is sent. Legal action remains a board decision.',
      },
      {
        icon: 'requests',
        title: 'Co-owner requests',
        availability: 'now',
        automated: [
          'Every request triaged: category, urgency, unit',
          'Draft reply citing the article and page of the declaration or by-laws',
          "Instant answers to co-owners' questions, around the clock, from the syndicate's documents",
          "Forwarded to the board when the documents don't answer",
        ],
        human: 'You review, edit or dismiss the draft. Nothing goes out in your name without you.',
      },
      {
        icon: 'documents',
        title: 'Documents and register',
        availability: 'now',
        automated: [
          'Declaration, by-laws, minutes and certificates filed and indexed',
          'Full-text search in French, page by page',
          'Register of units, co-owners and quote-parts, with history',
          'A record for each co-owner and occupant (units, history), invitations, board roles and positions (president, treasurer…) with term dates, access matching the role',
        ],
        human: 'You decide which documents are shared with co-owners and which stay with the board.',
      },
      {
        icon: 'maintenance',
        title: 'Maintenance',
        availability: 'now',
        automated: [
          'For each reported problem, a proposed work order: component, vendor of the right trade, priority',
          "Message to the vendor drafted, without co-owners' personal information",
          'A secure link for the vendor, no account: they accept, propose a date, attach photos and documents, and report the work done',
          'Quotes: up to 5 contractors invited, each with their own link; answers read by the agent (price, validity, exclusions) and compared in a table',
          'Contractor invoices received by email at the syndicate’s billing address, read, matched to the work order and checked: right contractor, work completed, amount within the estimate, number not repeated, correct GST/QST',
          'Preventive maintenance calendar, and a logbook fed by every job',
        ],
        human: 'You create the work order in one click, retain the quote, confirm the work is done and approve invoices (or let compliant invoices under an amount you set be approved on their own); payment stays at the bank. Emergencies are handled by a person.',
      },
      {
        icon: 'governance',
        title: 'Assemblies and board meetings',
        availability: 'now',
        automated: [
          'Agenda proposed from requests awaiting a decision, deadlines, work and follow-ups, with the wording of resolutions',
          "Notice of meeting to each co-owner, in their language, with proof of delivery and a check of the notice period",
          'Meeting recording transcribed automatically (or a Teams/Zoom transcript imported), split by agenda item',
          'Minutes drafted from the transcript and the notes; follow-ups carried to the next meeting',
          'Attendance register from the registry, proxies and live quorum; voting rights suspended after three months of arrears',
          "Votes weighted by quote-parts, each resolution's majority computed, result written into the minutes",
          'Board of directors: calendar of regular meetings, quorum and casting vote per your by-laws, written resolutions signed online by all directors',
          'Register of resolutions and minute book kept up to date automatically, ready to print',
        ],
        human: 'You choose the agenda items, approve the notice, chair, obtain consent to the recording and approve the minutes.',
      },
      {
        icon: 'compliance',
        title: 'Compliance',
        availability: 'now',
        automated: ['Bill 16 obligations tracked: maintenance logbook, contingency fund study, register', 'Reminders to the board 60, 30 and 7 days before each deadline, then when overdue', "Calendar of the syndicate's statutory deadlines", "Co-owners' insurance certificates: read automatically, tracked unit by unit, reminders before expiry"],
        human: 'You get the alerts in time and decide what to do.',
      },
      {
        icon: 'reservations',
        title: 'Reservations',
        availability: 'now',
        automated: [
          'Party room, pickleball court, gym or move elevator, booked by co-owners themselves',
          'Rules applied automatically: hours, duration, notice, quota per unit, fees, deposit, closures',
          "Double bookings impossible; fees charged to the unit's account, refunded when cancelled in time",
        ],
        human: 'You set the rules and approve the bookings that need it (e.g. moves). The rest takes care of itself.',
      },
    ],
  },

  flow: {
    title: 'How an agent works',
    lead: 'Take a late payment.',
    steps: [
      { title: 'It detects', text: 'The expected deposit is missing from the imported bank statement.' },
      { title: 'It prepares', text: "It works out the balance, picks the scheduled reminder step and writes the notice in the co-owner's language." },
      { title: 'You approve', text: 'The batch of reminders waits in your approval queue. One click to approve or reject.' },
      { title: 'It acts and records', text: 'Emails go out with proof of delivery. Every step is logged: inputs, sources, approver.' },
    ],
  },

  humans: {
    title: 'What always stays human',
    lead: 'Some decisions call for judgment, presence or legal responsibility. Syndiqia never makes them for you.',
    items: ['Contingency fund strategy', 'Special assessments', 'By-law enforcement and fines', 'Disputes between co-owners', 'On-site emergencies', 'Anything heading to court'],
    approvalTitle: 'Approval queue',
    approvalItems: ['Money movements', 'Fines and penalties', 'Legal notices', 'Messages to all co-owners'],
  },

  trust: {
    title: 'Designed for Quebec condominiums',
    items: [
      { title: 'Civil Code of Québec', text: 'Quote-parts at the core: allocation of charges, weighted votes, quorum.' },
      { title: "Owners' associations", text: 'Horizontal developments organized as non-profits with servitudes: meeting rules from your by-laws, members’ vocabulary, without the Bill 16 obligations.' },
      { title: 'Separate funds', text: 'Operating fund and contingency fund strictly separate, as the law requires.' },
      { title: 'Tamper-proof ledger', text: 'Double-entry accounting; an entry is never edited, only reversed.' },
      { title: 'Law 25', text: 'Data hosted in Canada, role-based access, a log of actions.' },
      { title: 'Syndicates isolated', text: 'Each syndicate is walled off within the database itself.' },
      { title: 'Accountable agents', text: 'Agents’ proposals, the documents they cite, what they did on their own and who approved what are all logged. The log of events and automatic actions is chained by fingerprints: any later change is detectable, and the board can verify it.' },
    ],
  },

  offers: {
    title: 'Three plans, one platform',
    lead: 'The same agents in every plan. What changes: who follows up and who goes on site. Prices per door (unit or lot) per month.',
    plans: [
      {
        name: 'Autonome',
        audience: 'Small self-managed syndicates and associations: the board stays in charge',
        price: '$6 per door per month',
        priceNote: 'Minimum $49 a month. Setup: free self-serve, $490 with assisted import',
        features: ['The whole platform and its agents', 'App for the board and owners', 'Email support and webinars'],
      },
      {
        name: 'Assistée',
        audience: 'Boards that want the work done, without on-site needs',
        price: '$17 per door per month',
        priceNote: 'Minimum $350 a month. Setup: $990, waived with a 2-year contract',
        features: ['Everything in Autonome', 'A remote Syndiqia manager: phone, review of the agents’ work', 'Budget, year-end package for the accountant', 'Meeting preparation and remote attendance'],
        highlight: true,
      },
      {
        name: 'Complète',
        audience: 'Boards leaving a full-service management firm',
        price: '$25 per door per month',
        priceNote: 'Minimum $500 a month. Setup: $1,490, waived with a 2-year contract',
        features: ['Everything in Assistée', 'Site visits', 'Contractor supervision', 'Emergencies 24/7'],
      },
    ],
    terms: [
      'Free 3-month trial, with your real data',
      'No termination fee; full export of your data at any time',
      'Price locked for 24 months',
      'No supplier commissions; the money stays in the syndicate’s or association’s bank account',
      'Same price for associations and horizontal developments: no surcharge',
    ],
    note: 'Pilot-phase pricing, plus taxes. Pre-authorized debit fees are flat, never a percentage.',
  },

  faq: {
    title: 'Frequently asked questions',
    items: [
      { q: 'Can an agent send a message or pay without us?', a: "No. Money movements, legal notices, fines and messages to all co-owners always go through the board's approval queue." },
      { q: "Where do the answers to co-owners come from?", a: "Only from your syndicate's documents shared with co-owners: declaration, by-laws, minutes. Every answer cites the article and page. When the documents don't answer, the question goes to the board." },
      { q: 'Is our data protected?', a: "Data and files are hosted in Canada; they are walled off per syndicate or association within the database, and each person's access is limited to their role. To read a document or draft an answer, text excerpts are processed by Amazon's artificial intelligence service, which may process them outside Quebec: they are not kept there and are not used to train models." },
      { q: 'What about our accountant?', a: 'The ledger follows a chart of accounts built for syndicates; export to QuickBooks and Sage is planned.' },
      { q: 'Can the agents act on their own?', a: 'Yes, process by process, when you are ready. Everything starts in confirmation mode; the platform measures each process’s accuracy (how many proposals you confirm as they are) and flags when it is ready. A director then switches it to automatic: answers to questions, payment reminders, bank reconciliation, insurance certificates, sending work to contractors up to the amount you set, approving compliant invoices and bookings. Guardrails stay on, every action is logged and summarized each morning by email, and you can go back to confirmation in one click.' },
      { q: 'Do co-owners have to use the app?', a: 'No. Each syndicate has its own email address: an email becomes a request, sorted and handled like the others, and co-owners answer the board simply by replying to its emails.' },
      { q: 'Can the Syndiqia team see our data?', a: 'No, not without your consent. If you ask for help, support requests temporary access; a director approves or refuses it. The access is read-only, every page viewed is logged and you can withdraw it at any time.' },
      { q: 'Do we need training?', a: 'No. Every screen has its help ("?") with a task-based guide matched to your role, and the assistant also answers "how do I…" questions about Syndiqia.' },
      { q: 'How does onboarding work?', a: 'You give us what you have, in whatever format you have: by-laws and other documents as PDF or Word, the owners list in Excel, financial statements. With our team, in six steps (identity, documents, owners, finances, suppliers, board), Syndiqia reads everything, tells you what is complete and what is missing (“12 owners have no phone number”), and shows you what it understood. You check and apply; nothing is saved before. Then we invite the members.' },
      { q: 'We are under contract with a management firm. Is it too late?', a: 'No. The 3-month trial runs in parallel, during your notice period: you compare on your real files, then we request the records from your current manager ourselves. The takeover takes 30 days.' },
      { q: 'What if Syndiqia went out of business?', a: 'Your data is yours: you can get a full export at any time, at no charge. Your money never goes through Syndiqia: it stays in your own bank account.' },
    ],
  },

  closing: {
    title: 'Less administration, better decisions.',
    text: 'Explore the demos: a fictional syndicate of 24 apartments and an owners’ association of a domain, with their finances, requests and documents. Then try Syndiqia free for 3 months with your own data.',
  },
  confidential: 'Presentation page for signed-in users during the demonstration phase.',
  back: 'Back to the app',
};

export const ABOUT: Record<'fr' | 'en', AboutContent> = { fr, en };
